import { useEffect } from "react";
import { MarketRate, OrderSide, OrderType, PairSymbol, TradeForm } from "@cede/types";
import { API_ENDPOINT, getCurrentRate, unformatNumberAsString } from "@cede/utils";
import { useMultipleTransactionsMinAmount } from "../multi-transaction/useMultipleTransactionsMinAmount";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { computeMinAmountInFromCurrency } from "@/utils/trade/minAmount";
import { useQuery } from "@tanstack/react-query";

type UseTradeMinAmountParams = {
  tradeForm: TradeForm;
  marketRate: MarketRate;
  shouldUseTradeSquare: boolean;
  orderType: OrderType;
};

export const useTradeMinAmount = ({
  tradeForm,
  marketRate,
  shouldUseTradeSquare,
  orderType,
}: UseTradeMinAmountParams) => {
  const apiHelper = useApi();
  const { auth } = useHubUser();

  const { isLoading: multipleTransactionMinAmountIsLoading, minAmount: multipleTransactionsMinAmount } =
    useMultipleTransactionsMinAmount({
      enabled: shouldUseTradeSquare,
      accountId: tradeForm.selectedAccount?.value || "",
      fromToken: tradeForm.tokenFrom?.value || "",
      toToken: tradeForm.tokenTo?.value || "",
    });

  /**
   * Find the minimum amount to trade for the tokenFrom
   */
  const getMinAmountFrom = async (pairSymbol: PairSymbol, orderSide: OrderSide): Promise<string | undefined> => {
    const price =
      orderType === OrderType.MARKET
        ? getCurrentRate(marketRate, orderSide)
        : unformatNumberAsString(tradeForm.limitPrice).toString();

    if (!tradeForm.selectedAccount || price === "0" || !auth.identityToken) return undefined;

    const minAmounts = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_MIN_AMOUNTS, {
      queryParams: {
        accountId: tradeForm.selectedAccount.value,
        pairSymbol,
        orderSide,
        price,
      },
    });

    const minAmount = computeMinAmountInFromCurrency(minAmounts, orderSide, price);

    // we add 20% of margin as the price can fluctuate, so it can pass below min amount
    const minAmountWithMargin = minAmount.mul(1.2);
    return minAmountWithMargin.toString();
  };

  // Limit orders use the user-provided price, so we can always compute min amount.
  // Market orders rely on the WebSocket market rate, so we must wait until it's available.
  const hasPriceForMinAmountQuery = orderType === OrderType.LIMIT || (!!marketRate?.bid && !!marketRate?.ask);

  const {
    data: classicMinAmount,
    isLoading: classicMinAmountIsLoading,
    refetch: refetchClassicMinAmount,
  } = useQuery({
    queryKey: [
      "tradeMinAmount",
      tradeForm.market?.pairSymbol,
      tradeForm.orderSide,
      tradeForm.limitPrice,
      orderType,
      auth.identityToken,
      tradeForm.selectedAccount?.value,
    ],
    queryFn: async () => {
      if (!tradeForm.orderSide) return null;
      return (await getMinAmountFrom(tradeForm.market?.pairSymbol || "", tradeForm.orderSide)) ?? null;
    },
    enabled:
      !!auth.identityToken &&
      !!tradeForm.market?.pairSymbol &&
      !!tradeForm.orderSide &&
      !!tradeForm.selectedAccount &&
      !shouldUseTradeSquare &&
      hasPriceForMinAmountQuery,
  });

  const minAmount = shouldUseTradeSquare ? multipleTransactionsMinAmount : classicMinAmount;

  // we need to refetch the min amount when the market rate changes
  // /!\ We shouldn't put the market rate as dependency of the useQuery because the min amount
  // will have the undefined value during the fetching, which could lead to clipping issues
  useEffect(() => {
    // the classic min amount is not needed if we use the trade square
    if (shouldUseTradeSquare) return;
    refetchClassicMinAmount();
  }, [marketRate]);

  return {
    minAmount: minAmount ?? undefined,
    isLoading: shouldUseTradeSquare ? multipleTransactionMinAmountIsLoading : classicMinAmountIsLoading,
  };
};
